/**
 * Mapping + formatting layer behind the investor-book popover (PT-5705, PT-5708).
 *
 * The book is now read from the server: `PammOfferType.investors` returns the real investors of
 * the node, and the API only ever returns it for nodes the viewer owns — that ownership check is
 * the actual control, not the hidden trigger on the card (PT-5708). This module's only job is to
 * turn that payload into the shape the table renders, and to format it.
 *
 * `funds` is each investor's successful deposits and `share` their percent of the node AUM, both
 * computed server-side from the same rows, so the Funds column totals the AUM shown on the card
 * and the Share column totals 100%.
 */

/** En-dash shown wherever a value is missing or undefined (AC: empty/edge states, PT-5706). */
export const PLACEHOLDER = '–'

export type Investor = {
  id: string
  name: string
  /** Whole currency units the investor funds the offer with. */
  funds: number
  /** Percent of node AUM, exact (unrounded); `null` when AUM is 0 (share is undefined, shown as "–"). */
  share: number | null
  /** `null` renders as "–" — tolerates a missing join date. */
  created: Date | null
  /** `null`/empty renders as "–" (no broken mailto) — tolerates a missing address. */
  email: string | null
  /**
   * Whether `email` is masked (`j***@domain`) because this viewer is an upstream agent who does
   * not own the node (PT-5709). A masked address is not a real one, so the row renders it as
   * plain text — a `mailto:` link would offer to write to an address that does not exist.
   */
  emailMasked: boolean
}

/** The server's investor row; mirrors `PammInvestorType` but keeps this module free of codegen types. */
export type InvestorPayload = {
  id?: number | null
  name?: string | null
  funds?: number | null
  share?: number | null
  created?: string | null
  email?: string | null
  emailMasked?: boolean | null
}

/** Parses an ISO-8601 timestamp, yielding `null` for a missing or unparseable value. */
const toDate = (value?: string | null): Date | null => {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * Maps the offer's `investors` payload into the rows the table renders.
 *
 * Every field is treated as nullable: the schema makes them all optional, and PT-5706 requires a
 * missing value to render as "–" rather than break the row. `funds` falls back to 0 (an investor
 * with no successful deposits still belongs in the book), while `share` and `created` stay `null`
 * when absent, because 0 and the epoch would both be lies.
 *
 * @param investors - The offer's `investors` field; `null`/`undefined` yields an empty book.
 * @returns The investor rows, in the order the server returned them (the table applies the sort).
 */
export const toInvestorBook = (investors?: (InvestorPayload | null)[] | null): Investor[] =>
  (investors ?? []).filter((investor): investor is InvestorPayload => investor != null).map((investor, index) => ({
    // The id keys the table rows. It is non-null in every server payload, but the schema types it
    // optional, and two id-less rows would otherwise collide on the same key ('') — React would
    // then reuse one row's DOM for the other's data while sorting. Fall back to the position.
    id: investor.id != null ? String(investor.id) : `row-${index}`,
    name: investor.name ?? PLACEHOLDER,
    funds: investor.funds ?? 0,
    share: investor.share ?? null,
    created: toDate(investor.created),
    email: investor.email ?? null,
    // Defaults to masked, not unmasked: if the server did not say, the safe reading is that the
    // address may not be real, so we withhold the mailto rather than offer a dead link (PT-5709).
    emailMasked: investor.emailMasked ?? true,
  }))

/** Currency amount with thousands separators and no decimals (AC: "Funds: USD, no decimals"). */
export const formatFunds = (amount: number, currency: string, locale?: string): string =>
  Number.isFinite(amount)
    ? new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount)
    : PLACEHOLDER

/** Share percentage, always 2 decimals (AC); "–" when the share is undefined (e.g. AUM 0). */
export const formatShare = (percent: number | null | undefined): string =>
  percent != null && Number.isFinite(percent) ? `${percent.toFixed(2)}%` : PLACEHOLDER

/**
 * Date as `DD MMM YYYY` in the viewer's locale — the month name is localised but the field
 * order is always day–month–year regardless of the locale's default pattern. A missing or
 * invalid date renders as "–" (PT-5706).
 *
 * @param date - The date to format; `null`/invalid yields "–".
 * @param locale - Optional locale; defaults to the runtime (user) locale.
 */
export const formatCreated = (date: Date | null | undefined, locale?: string): string => {
  if (!date || Number.isNaN(date.getTime())) return PLACEHOLDER
  const parts = new Intl.DateTimeFormat(locale, {day: '2-digit', month: 'short', year: 'numeric'}).formatToParts(date)
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? ''
  return `${get('day')} ${get('month')} ${get('year')}`
}
